import React, { useRef, useEffect, useMemo } from 'react';
import { Message } from '../../types/workflow.types';

/**
 * Simple markdown renderer — converts basic markdown to HTML.
 * Supports: **bold**, *italic*, `code`, [links](url), bullet lists, and line breaks.
 */
function renderMarkdown(text: string): string {
  let html = text
    // Escape HTML entities first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_ (but not inside bold)
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    // Inline code: `code`
    .replace(/`(.+?)`/g, '<code style="background:rgba(0,0,0,0.06);padding:1px 5px;border-radius:4px;font-size:0.9em;">$1</code>')
    // Links: [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#007aff;text-decoration:underline;">$1</a>')
    // Bullet lists: lines starting with • or -
    .replace(/^[•\-]\s+(.+)$/gm, '<li style="margin-left:16px;list-style:disc;">$1</li>')
    // Line breaks
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li[^>]*>.*?<\/li><br\/>?)+)/g, (match) => {
    const cleaned = match.replace(/<br\/?>/g, '');
    return `<ul style="margin:4px 0;padding-left:8px;">${cleaned}</ul>`;
  });

  return html;
}

/**
 * Props interface for the MessageList component
 */
interface MessageListProps {
  // Message data
  messages: Message[];
  
  // Loading states
  loading: boolean;
  isLoadingState: boolean;
  getLoadingMessage: () => string;
  
  // Optional children rendered inside the scrollable area (e.g. StructuredCaptureCard)
  children?: React.ReactNode;
}

/**
 * Individual Message Bubble Component
 * Renders a single message with appropriate styling based on the role
 */
interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  /**
   * Renders the appropriate avatar based on message role
   */
  const renderAvatar = () => {
    switch (message.role) {
      case "user":
        return <span className="chatbot-avatar-text">You</span>;
      
      case "system":
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
            />
          </svg>
        );
      
      case "bot":
      default:
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
            />
          </svg>
        );
    }
  };

  return (
    <div className={`chatbot-message chatbot-message-enter ${message.role === "user" ? "user" : ""}`}>
      {/* Avatar */}
      <div className={`chatbot-avatar ${message.role}`}>
        {renderAvatar()}
      </div>

      {/* Message Content */}
      <div className={`chatbot-message-content ${message.role === "user" ? "user" : ""}`}>
        <div className={`chatbot-message-bubble ${message.role}`}>
          <div 
            className="chatbot-message-text"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
        </div>
        <span className="chatbot-message-time">
          {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </div>
  );
};

/**
 * Typing Indicator Component
 * Shows animated dots when the bot is processing a response
 */
const TypingIndicator: React.FC<{ isLoadingState: boolean; getLoadingMessage: () => string }> = ({ 
  isLoadingState, 
  getLoadingMessage 
}) => (
  <div className="chatbot-message chatbot-message-enter">
    <div className="chatbot-avatar bot">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
        />
      </svg>
    </div>
    <div className="chatbot-message-content">
      <div className="chatbot-message-bubble bot">
        {/* Animated typing dots */}
        <div className="chatbot-typing">
          <div className="chatbot-typing-dot"></div>
          <div className="chatbot-typing-dot"></div>
          <div className="chatbot-typing-dot"></div>
        </div>
        {/* Loading message if available */}
        {isLoadingState && (
          <p className="chatbot-loading-text">{getLoadingMessage()}</p>
        )}
      </div>
    </div>
  </div>
);

/**
 * MessageList Component
 * 
 * Displays the conversation messages in a scrollable container.
 * Features:
 * - Auto-scrolls to bottom when new messages arrive
 * - Shows typing indicator during loading states
 * - Renders different message types (user, bot, system) with appropriate styling
 * - Displays timestamps for each message
 * 
 * This component extracts the message display logic from the main FillyOnIris component
 * to improve maintainability and reusability.
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  loading,
  isLoadingState,
  getLoadingMessage,
  children
}) => {
  // Ref for auto-scrolling to the bottom of messages
  const messagesEndRef = useRef<HTMLDivElement>(null);

  /**
   * Auto-scroll to bottom when messages change or loading state changes
   * This ensures users always see the latest message or loading indicator
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, isLoadingState]);

  return (
    <div className="chatbot-messages">
      <div className="chatbot-messages-content">
        {/* Render all messages */}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* Show typing indicator when loading */}
        {(loading || isLoadingState) && (
          <TypingIndicator 
            isLoadingState={isLoadingState} 
            getLoadingMessage={getLoadingMessage} 
          />
        )}

        {/* Inline content (e.g. StructuredCaptureCard) rendered inside scrollable area */}
        {children}

        {/* Invisible element for auto-scrolling */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;
